// i18n.js — JSON-backed string lookup. Twenty lines of logic.
//
// Languages are JSON files in i18n/{lang}.json (repo root). build.mjs copies
// i18n/ → dist/i18n/ verbatim, so the source path and the deployed path match;
// Keys are dotted paths. Missing keys fall back to English, then to the key
// itself.
//
// Brief 105: the data lived under src/i18n/ historically and the loader fetched
// /src/i18n/{lang}.json. That worked in dev (esbuild watcher serves the source
// tree as-is so /src/i18n/*.json resolved) but failed in production — esbuild
// bundles the loader into /app.<hash>.js and the build output puts the JSON at
// /i18n/, not /src/i18n/. Test server needed a server-side symlink to bridge.
// Brief 105 moved the JSON to i18n/ at repo root (matches the manifest.web-
// manifest / favicon.ico convention — static data at root, build.mjs copies to
// dist root), aligning the dev path and the deploy path.

/** @type {Record<string, Record<string, string>>} */
const cache = {};
let current = "en";
/** @type {Record<string, string>} */
let strings = {};
/** @type {Record<string, string>} */
let fallback = {};

/** @param {string} lang */
export async function setLanguage(lang) {
  if (!cache[lang]) {
    const res = await fetch(`/i18n/${lang}.json`);
    cache[lang] = await res.json();
  }
  if (!cache.en) {
    const res = await fetch(`/i18n/en.json`);
    cache.en = await res.json();
  }
  current = lang;
  strings = cache[lang] ?? {};
  fallback = cache.en ?? {};
  document.documentElement.lang = lang;
  // Tell mounted components to re-render with the freshly-loaded strings.
  // Components that registered via t() during their first render (before
  // setLanguage finished, e.g. <kfe-app-shell>) need this; otherwise their
  // header/labels stay frozen as raw keys like "app.title".
  document.dispatchEvent(new Event("kfe:i18n"));
}

/**
 * @param {string} key e.g. "pathway.title"
 * @param {Record<string, string|number>} [vars]
 * @returns {string}
 */
export function t(key, vars) {
  const raw = strings[key] ?? fallback[key] ?? key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, k) => String(vars[k] ?? `{${k}}`));
}

export function getLanguage() { return current; }
